import { Namespaces } from 'domain/common/namespaces';

import config from 'config';

import { MAGIC_AUTO_COLUMN_WIDTH } from '../leaderboard-column-model';

import type { ColumnIdentifier, LeaderboardColumn } from './column-model';
import { columnIdentityComparator } from './column-model-utils';

export const NEPTUNE_COLUMN_IDS = {
  GROUP_BY: 'neptune-group-by',
  SELECTION: 'neptune-selection',
  COMPARE: 'neptune-compare',
  CREATION_TIME: `${Namespaces.System}/creation_time`,
  ID: `${Namespaces.System}/id`,
  OWNER: `${Namespaces.System}/owner`,
  MONITORING_TIME: `${Namespaces.System}/monitoring_time`,
  NAME: `${Namespaces.System}/name`,
  SIZE: `${Namespaces.System}/size`,
  STATE: `${Namespaces.System}/state`,
  RUNNING_TIME_SECONDS: `${Namespaces.System}/running_time_seconds`,
  EXPERIMENT_RUNNING_TIME: `${Namespaces.System}/experiment/running_time_seconds`,
  TAGS: `${Namespaces.System}/tags`,
  TYPE: 'neptune-type',
  DESCRIPTION: `${Namespaces.System}/description`,
  GROUP_TAGS: `${Namespaces.System}/group_tags`,
};

export type ColumnSettings = ColumnIdentifier & LeaderboardColumn & { visibleByDefault?: boolean };

const getColumnIdsFromConfig = (configColumns: string | undefined): string[] => {
  if (!configColumns) {
    return [];
  }

  const columns = configColumns.split(',').map((s) => s.trim());

  return columns;
};

export function isNameColumn(columnId: string): boolean {
  return columnId === NEPTUNE_COLUMN_IDS.NAME;
}

export const localTableStateColumns = [
  NEPTUNE_COLUMN_IDS.GROUP_BY,
  NEPTUNE_COLUMN_IDS.SELECTION,
  NEPTUNE_COLUMN_IDS.COMPARE,
];

const visibleColumns: readonly string[] = getColumnIdsFromConfig(
  config.defaultVisibleObjectTableFields,
);

const pinnedColumns: readonly string[] = getColumnIdsFromConfig(
  config.defaultPinnedObjectTableFields,
);

const builtinColumnsSettings: ColumnSettings[] = [
  {
    id: NEPTUNE_COLUMN_IDS.GROUP_BY,

    // this type is just virtual as this column is represented by its own specific renderer
    columnType: 'string',
    displayName: 'Group By',
    exportable: false,
    groupable: false,
    hideable: false,
    pinned: true,
    reorderable: false,
    resizable: true,
    sortable: false,
    width: 150,
    visibleByDefault: true,
  },
  {
    id: NEPTUNE_COLUMN_IDS.SELECTION,
    // this type is just virtual as this column is represented by its own specific renderer
    columnType: 'string',
    exportable: false,
    groupable: false,
    hideable: false,
    pinned: true,
    reorderable: false,
    resizable: false,
    sortable: false,
    width: 24,
    visibleByDefault: true,
  },
  {
    id: NEPTUNE_COLUMN_IDS.COMPARE,
    // this type is just virtual as this column is represented by its own specific renderer
    columnType: 'string',
    exportable: false,
    groupable: false,
    hideable: false,
    pinned: true,
    reorderable: false,
    resizable: false,
    sortable: false,
    width: 40,
    visibleByDefault: true,
  },
  {
    id: NEPTUNE_COLUMN_IDS.TYPE,
    columnType: 'string',
    displayName: 'Type',
    exportable: true,
    groupable: false,
    hideable: false,
    pinned: true,
    reorderable: false,
    resizable: true,
    sortable: false,
    width: 110,
  },
  {
    id: NEPTUNE_COLUMN_IDS.NAME,
    columnType: 'string',
    exportable: true,
    groupable: true,
    hideable: false,
    pinned: pinnedColumns.includes(NEPTUNE_COLUMN_IDS.NAME),
    reorderable: {
      draggable: false,
      droppable: { after: true },
    },
    resizable: true,
    sortable: true,
    width: MAGIC_AUTO_COLUMN_WIDTH,
    autoWidthEnabled: true,
    visibleByDefault: visibleColumns.includes(NEPTUNE_COLUMN_IDS.NAME),
  },
  {
    id: NEPTUNE_COLUMN_IDS.ID,
    columnType: 'string',
    exportable: true,
    groupable: false,
    hideable: true,
    pinned: pinnedColumns.includes(NEPTUNE_COLUMN_IDS.ID),
    reorderable: true,
    resizable: true,
    sortable: true,
    width: 200,
    visibleByDefault: visibleColumns.includes(NEPTUNE_COLUMN_IDS.ID),
  },
  {
    id: NEPTUNE_COLUMN_IDS.CREATION_TIME,
    columnType: 'datetime',
    exportable: true,
    groupable: false,
    hideable: true,
    pinned: pinnedColumns.includes(NEPTUNE_COLUMN_IDS.CREATION_TIME),
    reorderable: true,
    resizable: true,
    sortable: true,
    width: 165,
    visibleByDefault: visibleColumns.includes(NEPTUNE_COLUMN_IDS.CREATION_TIME),
  },
  {
    id: NEPTUNE_COLUMN_IDS.OWNER,
    columnType: 'string',
    exportable: true,
    groupable: true,
    hideable: true,
    pinned: pinnedColumns.includes(NEPTUNE_COLUMN_IDS.OWNER),
    reorderable: true,
    resizable: true,
    sortable: true,
    width: 150,
    visibleByDefault: visibleColumns.includes(NEPTUNE_COLUMN_IDS.OWNER),
  },
  {
    id: NEPTUNE_COLUMN_IDS.STATE,
    columnType: 'string',
    exportable: true,
    groupable: true,
    hideable: true,
    pinned: pinnedColumns.includes(NEPTUNE_COLUMN_IDS.STATE),
    reorderable: true,
    resizable: true,
    sortable: true,
    width: 120,
    visibleByDefault: visibleColumns.includes(NEPTUNE_COLUMN_IDS.STATE),
  },
  {
    id: NEPTUNE_COLUMN_IDS.MONITORING_TIME,
    columnType: 'int',
    exportable: true,
    groupable: false,
    hideable: true,
    pinned: pinnedColumns.includes(NEPTUNE_COLUMN_IDS.MONITORING_TIME),
    reorderable: true,
    resizable: true,
    sortable: true,
    width: 140,
    visibleByDefault: visibleColumns.includes(NEPTUNE_COLUMN_IDS.MONITORING_TIME),
  },
  {
    id: NEPTUNE_COLUMN_IDS.TAGS,
    columnType: 'stringSet',
    exportable: true,
    groupable: true,
    hideable: true,
    pinned: pinnedColumns.includes(NEPTUNE_COLUMN_IDS.TAGS),
    reorderable: true,
    resizable: true,
    sortable: false,
    width: 200,
    visibleByDefault: visibleColumns.includes(NEPTUNE_COLUMN_IDS.TAGS),
  },
  {
    id: NEPTUNE_COLUMN_IDS.DESCRIPTION,
    columnType: 'string',
    exportable: true,
    groupable: true,
    hideable: true,
    pinned: pinnedColumns.includes(NEPTUNE_COLUMN_IDS.DESCRIPTION),
    reorderable: true,
    resizable: true,
    sortable: false,
    width: 200,
    visibleByDefault: visibleColumns.includes(NEPTUNE_COLUMN_IDS.DESCRIPTION),
  },
  {
    id: NEPTUNE_COLUMN_IDS.GROUP_TAGS,
    columnType: 'stringSet',
    exportable: true,
    groupable: true,
    hideable: true,
    pinned: pinnedColumns.includes(NEPTUNE_COLUMN_IDS.GROUP_TAGS),
    reorderable: true,
    resizable: true,
    sortable: false,
    width: 200,
    visibleByDefault: visibleColumns.includes(NEPTUNE_COLUMN_IDS.GROUP_TAGS),
  },
];

export const defaultVisibleColumnsSettings = builtinColumnsSettings.filter(
  (colSetting) => colSetting.visibleByDefault,
);

export function getBuiltinColumnSettings(
  columnIdentifier: ColumnIdentifier,
): ColumnSettings | undefined {
  return builtinColumnsSettings.find((colSettings) =>
    columnIdentityComparator(colSettings, columnIdentifier),
  );
}

export function getBuiltInColumnSettingsById(id: string): ColumnSettings | undefined {
  return builtinColumnsSettings.find((colSettings) => colSettings.id === id);
}

export function isColumnExportable(id: string): boolean {
  return getBuiltInColumnSettingsById(id)?.exportable !== false;
}
